import React from 'react';
import { Alert, Form } from 'react-bootstrap';
import { groupPartRows, mappedMPNs, validMapping } from '../services/partMapping';

export default function PartMappingFields({ columns, value, onChange, disabled = false, rows = [], id = 'part-mapping' }) {
  const mapped = value.secondary_column !== null;
  const update = patch => onChange({ ...value, ...patch });
  const preview = validMapping(value, columns)
    ? groupPartRows(rows, value, columns).slice(0, 3) : [];
  return <fieldset disabled={disabled} className="border rounded p-3 mb-3">
    <legend className="float-none w-auto px-2 fs-5">How are part numbers organized?</legend>
    <p className="text-muted">Choose the layout in your file. Your original rows and quantities are always preserved.</p>
    <div className="bomwise-mapping-choices">
      <Form.Check type="radio" id={`${id}-one`} name={`${id}-layout`}
        className={`bomwise-mapping-choice ${!mapped ? 'is-selected' : ''}`}
        label={<><strong>One part-number column</strong><small>Each row has one MPN or part number.<br />Example: RC0603FR-0710KL</small></>}
        checked={!mapped} onChange={() => update({ secondary_column: null, secondary_format: '' })} />
      <Form.Check type="radio" id={`${id}-mapped`} name={`${id}-layout`}
        className={`bomwise-mapping-choice ${mapped ? 'is-selected' : ''}`}
        label={<><strong>Primary part number with mapped MPNs</strong><small>Usually an internal PN mapped to manufacturer parts.<br />Example: RES-001 → MPN-A, MPN-B</small></>}
        checked={mapped} onChange={() => update({ secondary_column: '', secondary_format: '' })} />
    </div>
    <Form.Group className="mt-3" controlId={`${id}-primary`}>
      <Form.Label>Primary part number column</Form.Label>
      <Form.Select value={value.primary_column} onChange={event => update({
        primary_column: event.target.value,
        ...(event.target.value === value.secondary_column ? { secondary_column: '' } : {})
      })}>
        <option value="">Choose a column…</option>
        {columns.map(column => <option key={column.id} value={column.id}>{column.name}</option>)}
      </Form.Select>
      <Form.Text>{mapped ? 'Usually your internal PN. Repeated values become one expandable top-level entry.'
        : 'Usually the manufacturer part number (MPN). Each source row stays a separate line.'}</Form.Text>
    </Form.Group>
    {mapped && <>
      <Form.Group className="mt-3" controlId={`${id}-secondary`}>
        <Form.Label>Manufacturer part number / secondary column</Form.Label>
        <Form.Select value={value.secondary_column} onChange={event => update({ secondary_column: event.target.value })}>
          <option value="">Choose a different column…</option>
          {columns.filter(column => column.id !== value.primary_column).map(column =>
            <option key={column.id} value={column.id}>{column.name}</option>)}
        </Form.Select>
      </Form.Group>
      <fieldset className="mt-3">
        <legend className="fs-6">How are MPNs listed in that column?</legend>
        <Form.Check type="radio" name={`${id}-format`} id={`${id}-single`}
          label="One MPN per row — RES-001 appears on separate rows for MPN-A and MPN-B"
          checked={value.secondary_format === 'single'} onChange={() => update({ secondary_format: 'single' })} />
        <Form.Check type="radio" name={`${id}-format`} id={`${id}-comma`}
          label="Comma-separated MPNs in each cell — one cell contains MPN-A, MPN-B"
          checked={value.secondary_format === 'comma'} onChange={() => update({ secondary_format: 'comma' })} />
      </fieldset>
    </>}
    {!validMapping(value, columns) && <p className="text-muted mt-3 mb-0" role="status">
      Choose {mapped ? 'both columns and an MPN format' : 'the primary column'} to continue.</p>}
    {!!preview.length && <Alert variant="light" className="border mt-3 mb-0" aria-label="Part-number layout preview">
      <strong>How your BOM will look</strong><small className="d-block text-muted">Sample from the first 10 source rows</small>
      <ul className="mb-0 mt-2">{preview.map(row => <li key={row.line_item_id}>
        <strong>{String(row.data[value.primary_column] ?? '').trim() || '(Missing primary part number)'}</strong>
        {mapped && <ul>{row.sourceRows.flatMap(source => mappedMPNs(source, value).map((mpn, index) =>
          <li key={`${source.line_item_id}:${index}`}>{mpn}</li>))}
          {!row.sourceRows.some(source => mappedMPNs(source, value).length) && <li>No mapped MPNs</li>}
        </ul>}
      </li>)}</ul>
    </Alert>}
  </fieldset>;
}
